<template>
  <div class="bg-white shadow-sm border border-slate-200 overflow-hidden">
    <div class="px-6 pt-2 pb-1">
      <div class="flex items-baseline gap-2 flex-wrap">
        <h2 class="text-base font-bold text-[#0d4169] text-left">
          {{ resolvedCompanyName }}
        </h2>
        <span class="text-sm font-bold text-slate-600">Withholding</span>
      </div>
    </div>

    <FinanceToast :show="toast.show" :variant="toast.variant">{{ toast.message }}</FinanceToast>

    <div v-if="isLoading" class="px-6 py-12 flex justify-center items-center">
      <div class="h-8 w-8 border-2 border-[#0d4169]/30 border-t-transparent rounded-full animate-spin"></div>
    </div>

    <form v-else @submit.prevent="save" class="px-6 pt-5 pb-6">
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-10">
        <!-- With tin number -->
        <section class="flex flex-col gap-3">
          <h3 class="text-sm font-bold text-[#0d4169] text-left border-b border-slate-200 pb-1">
            With Tin Number
          </h3>
          <div class="grid grid-cols-[minmax(5.5rem,auto)_1fr] gap-x-3 gap-y-3 items-center">
            <div />
            <div class="grid grid-cols-2 gap-0">
              <span class="text-[0.72rem] font-semibold text-[#1e293b] text-center">More than</span>
              <span class="text-[0.72rem] font-semibold text-[#1e293b] text-center">%</span>
            </div>

            <span class="text-[0.78rem] font-semibold text-[#1e293b] text-left">for service</span>
            <div
              class="flex rounded-md border border-[#cbd5e1] overflow-hidden transition-all focus-within:border-[#0d4169] focus-within:ring-1 focus-within:ring-[#0d4169]/20"
            >
              <input
                v-model="form.withTinServiceMoreThan"
                type="text"
                class="flex-1 min-w-0 px-2.5 py-1.5 border-0 rounded-none outline-none text-sm bg-white"
                placeholder="Amount"
                required
              >
              <div class="w-px shrink-0 bg-[#cbd5e1]" aria-hidden="true" />
              <input
                v-model="form.withTinServicePercent"
                type="text"
                class="flex-1 min-w-0 px-2.5 py-1.5 border-0 rounded-none outline-none text-sm bg-white"
                placeholder="%"
                required
              >
            </div>

            <span class="text-[0.78rem] font-semibold text-[#1e293b] text-left">for goods</span>
            <div
              class="flex rounded-md border border-[#cbd5e1] overflow-hidden transition-all focus-within:border-[#0d4169] focus-within:ring-1 focus-within:ring-[#0d4169]/20"
            >
              <input
                v-model="form.withTinGoodsMoreThan"
                type="text"
                class="flex-1 min-w-0 px-2.5 py-1.5 border-0 rounded-none outline-none text-sm bg-white"
                placeholder="Amount"
                required
              >
              <div class="w-px shrink-0 bg-[#cbd5e1]" aria-hidden="true" />
              <input
                v-model="form.withTinGoodsPercent"
                type="text"
                class="flex-1 min-w-0 px-2.5 py-1.5 border-0 rounded-none outline-none text-sm bg-white"
                placeholder="%"
                required
              >
            </div>
          </div>
        </section>

        <!-- Without tin number -->
        <section class="flex flex-col gap-3">
          <h3 class="text-sm font-bold text-[#0d4169] text-left border-b border-slate-200 pb-1">
            Without Tin Number
          </h3>
          <div class="grid grid-cols-[minmax(5.5rem,auto)_1fr] gap-x-3 gap-y-3 items-center">
            <div />
            <div class="grid grid-cols-2 gap-0">
              <span class="text-[0.72rem] font-semibold text-[#1e293b] text-center">More than</span>
              <span class="text-[0.72rem] font-semibold text-[#1e293b] text-center">%</span>
            </div>

            <span class="text-[0.78rem] font-semibold text-[#1e293b] text-left">for service</span>
            <div
              class="flex rounded-md border border-[#cbd5e1] overflow-hidden transition-all focus-within:border-[#0d4169] focus-within:ring-1 focus-within:ring-[#0d4169]/20"
            >
              <input
                v-model="form.withoutTinServiceMoreThan"
                type="text"
                class="flex-1 min-w-0 px-2.5 py-1.5 border-0 rounded-none outline-none text-sm bg-white"
                placeholder="Amount"
              >
              <div class="w-px shrink-0 bg-[#cbd5e1]" aria-hidden="true" />
              <input
                v-model="form.withoutTinServicePercent"
                type="text"
                class="flex-1 min-w-0 px-2.5 py-1.5 border-0 rounded-none outline-none text-sm bg-white"
                placeholder="%"
                required
              >
            </div>

            <span class="text-[0.78rem] font-semibold text-[#1e293b] text-left">for goods</span>
            <div
              class="flex rounded-md border border-[#cbd5e1] overflow-hidden transition-all focus-within:border-[#0d4169] focus-within:ring-1 focus-within:ring-[#0d4169]/20"
            >
              <input
                v-model="form.withoutTinGoodsMoreThan"
                type="text"
                class="flex-1 min-w-0 px-2.5 py-1.5 border-0 rounded-none outline-none text-sm bg-white"
                placeholder="Amount"
              >
              <div class="w-px shrink-0 bg-[#cbd5e1]" aria-hidden="true" />
              <input
                v-model="form.withoutTinGoodsPercent"
                type="text"
                class="flex-1 min-w-0 px-2.5 py-1.5 border-0 rounded-none outline-none text-sm bg-white"
                placeholder="%"
                required
              >
            </div>
          </div>
        </section>
      </div>

      <div class="mt-8 flex justify-end">
        <button
          type="submit"
          :disabled="!canEditWithholding || isSaving"
          class="min-w-max px-5 py-1.5 bg-[#0d4169] text-white text-sm font-bold rounded-md shadow-md hover:bg-[#0a3354] transition-all active:scale-95 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          <div
            v-if="isSaving"
            class="h-5 w-5 border-2 border-white/30 border-t-transparent rounded-full animate-spin"
          ></div>
          <span v-if="!isSaving">Save</span>
        </button>
      </div>
    </form>
  </div>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from 'vue'
import { useStore } from 'vuex'
import FinanceToast from '../FinanceToast.vue'
import { apiFetch } from '../../../utils/api'

const FORM_KEYS = [
  'withTinServiceMoreThan',
  'withTinServicePercent',
  'withTinGoodsMoreThan',
  'withTinGoodsPercent',
  'withoutTinServiceMoreThan',
  'withoutTinServicePercent',
  'withoutTinGoodsMoreThan',
  'withoutTinGoodsPercent'
]

const store = useStore()

const currentUser = computed(() => store.getters.currentUser || store.state.user || {})

const userPrivileges = computed(() => {
  const raw = currentUser.value?.privilege
  if (!raw) return {}
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw)
    } catch {
      return {}
    }
  }
  return raw
})

const hasAllPrivilege = computed(() => {
  return userPrivileges.value?.All === true || userPrivileges.value?.all === true
})

function getPrivilegeEntry(key) {
  const priv = userPrivileges.value || {}
  if (!key) return null
  if (priv?.[key]) return priv[key]

  const normalizedNeedle = String(key).trim().toLowerCase()
  const foundKey = Object.keys(priv).find((k) => String(k).trim().toLowerCase() === normalizedNeedle)
  return foundKey ? priv[foundKey] : null
}

function getActionFlag(entry, action) {
  if (!entry || typeof entry !== 'object') return false
  if (!action) return false
  if (entry[action] !== undefined) return !!entry[action]

  const a = String(action)
  const candidates = [
    a.toLowerCase(),
    a.toUpperCase(),
    a.charAt(0).toUpperCase() + a.slice(1).toLowerCase()
  ]
  for (const k of candidates) {
    if (entry[k] !== undefined) return !!entry[k]
  }
  return false
}

const canEditWithholding = computed(() => {
  if (hasAllPrivilege.value) return true
  return getActionFlag(getPrivilegeEntry('Withholding'), 'Edit')
})

const resolvedCompanyName = computed(() => {
  const user = currentUser.value
  return (
    user?.company?.name ||
    user?.companyName ||
    user?.company_name ||
    user?.company?.company_name ||
    'Company Name'
  )
})

const form = reactive({
  withTinServiceMoreThan: '',
  withTinServicePercent: '',
  withTinGoodsMoreThan: '',
  withTinGoodsPercent: '',
  withoutTinServiceMoreThan: '',
  withoutTinServicePercent: '',
  withoutTinGoodsMoreThan: '',
  withoutTinGoodsPercent: ''
})

const isLoading = ref(false)
const isSaving = ref(false)
const hasExisting = ref(false)

const toast = reactive({
  show: false,
  message: '',
  variant: 'success'
})
let toastTimer = null

function showToast(message, variant = 'success') {
  if (toastTimer) {
    window.clearTimeout(toastTimer)
    toastTimer = null
  }
  toast.message = message
  toast.variant = variant
  toast.show = true
  const duration = variant === 'error' ? 5000 : 3000
  toastTimer = window.setTimeout(() => {
    toast.show = false
    toast.variant = 'success'
    toastTimer = null
  }, duration)
}

function resetForm() {
  for (const key of FORM_KEYS) {
    form[key] = ''
  }
}

function applyData(data) {
  for (const key of FORM_KEYS) {
    form[key] = data[key] != null ? String(data[key]) : ''
  }
}

function buildPayload() {
  const payload = {}
  for (const key of FORM_KEYS) {
    payload[key] = String(form[key] || '').trim()
  }
  return payload
}

function isPayloadValid(payload) {
  const requiredWithTin =
    payload.withTinServiceMoreThan &&
    payload.withTinServicePercent &&
    payload.withTinGoodsMoreThan &&
    payload.withTinGoodsPercent

  const requiredWithoutTinPercents =
    payload.withoutTinServicePercent && payload.withoutTinGoodsPercent

  return !!(requiredWithTin && requiredWithoutTinPercents)
}

async function loadWithholding() {
  isLoading.value = true
  try {
    const res = await apiFetch('/finance/withholding')
    if (!res.ok) {
      hasExisting.value = false
      resetForm()
      const err = await res.json().catch(() => ({}))
      showToast(err.message || 'Failed to load withholding settings.', 'error')
      return
    }
    const data = await res.json().catch(() => null)
    if (!data || typeof data !== 'object') {
      hasExisting.value = false
      resetForm()
      return
    }
    hasExisting.value = true
    applyData(data)
  } catch {
    hasExisting.value = false
    showToast('Failed to load withholding settings.', 'error')
  } finally {
    isLoading.value = false
  }
}

async function save() {
  if (isSaving.value || isLoading.value) return
  if (!canEditWithholding.value) return

  const payload = buildPayload()
  if (!isPayloadValid(payload)) return

  const wasExisting = hasExisting.value
  isSaving.value = true
  try {
    const res = await apiFetch('/finance/withholding', {
      method: 'PUT',
      body: JSON.stringify(payload)
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({}))
      showToast(err.message || 'Failed to save withholding settings.', 'error')
      return
    }
    hasExisting.value = true
    showToast(
      wasExisting
        ? 'Withholding settings updated successfully.'
        : 'Withholding settings saved successfully.',
      'success'
    )
    await loadWithholding()
  } catch {
    showToast('Failed to save withholding settings.', 'error')
  } finally {
    isSaving.value = false
  }
}

onMounted(() => {
  loadWithholding()
})
</script>
